import { useEffect, useMemo, type FC } from "react";
import { type Path } from "react-hook-form";
import styled from "styled-components";

import { useGetAggregatedEntityIds } from "@ds/hooks/use-get-aggregated-entity-ids";
import { useRootDispatch, useRootSelector } from "@ds/hooks";

import { EntityTypeName } from "@ds/model/entity-model";

import { selectCampaignById, selectCampaignsIsLoading } from "@ds/modules/content/campaigns/redux/selectors";
import { campaignsActions } from "@ds/modules/content/campaigns/redux/slice";
import { selectExperiencesByIds } from "@ds/modules/experiences/redux/selectors";
import { isExperience } from "@ds/modules/experiences/utils/model";

import { AggregatedValue } from "@ds/components/entities";
import { MultipleValuesLabelWithLink } from "@ds/components/forms/labels/common";
import { SpinnerIcon } from "@ds/components/loaders/loaders";
import { TextLink } from "@ds/components/text-link";
import { getProperty } from "@ds/utils/properties";
import { composeDetailsRouteByEntityIds, composeDetailsRouteByEntityType } from "@ds/utils/router";

import { selectProjectsByIds } from "../../redux/selectors/common-selectors";

const StyledSpinnerIcon = styled(SpinnerIcon)`
  width: 15px;
`;

export const CampaignValue: FC<{ value?: number }> = ({ value }) => {
  const campaign = useRootSelector(state => selectCampaignById(state, value || 0));
  const isLoading = useRootSelector(selectCampaignsIsLoading);

  const dispatch = useRootDispatch();
  useEffect(() => {
    if (value) {
      dispatch(campaignsActions.fetchCampaign(value, { shouldDisableSagaThrottling: true }));
    }
  }, [dispatch, value]);

  if (isLoading) return <StyledSpinnerIcon />;
  if (value && !campaign) return null;

  return <TextLink to={composeDetailsRouteByEntityType(campaign ?? null)}>{campaign?.name}</TextLink>;
};

export const FormationCampaignDisplayValue: FC<{
  entity: Experience | Project;
  field: string;
  isDirty?: boolean;
}> = ({ entity, field, isDirty = false }) => {
  const fieldPath = isExperience(entity)
    ? `formation.properties.${field}.value`
    : `template.experience_formation.properties.${field}.value`;

  const aggregatedEntityIds = useGetAggregatedEntityIds(entity);
  const aggregatedEntities = useRootSelector(state =>
    isExperience(entity)
      ? selectExperiencesByIds(state, aggregatedEntityIds)
      : selectProjectsByIds(state, aggregatedEntityIds),
  );

  const aggregatedFormationCampaignIds = useMemo(
    () => aggregatedEntities.map(entry => Number(getProperty(entry, fieldPath))),
    [aggregatedEntities, fieldPath],
  );

  return (
    <AggregatedValue
      entity={entity}
      field={fieldPath as Path<Experience> | Path<Project>}
      shouldShowEditedValue={isDirty}
      formatter={(entityEntry, entityField) => {
        const campaignValue = Number(getProperty(entityEntry, entityField));
        if (!campaignValue) {
          return undefined;
        }

        return <CampaignValue value={campaignValue} />;
      }}
      multipleFormatter={() => (
        <MultipleValuesLabelWithLink
          to={composeDetailsRouteByEntityIds(aggregatedFormationCampaignIds, EntityTypeName.CONTENT_CAMPAIGN)}
        />
      )}
    />
  );
};
